/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

// eslint-disable-next-line import/no-restricted-paths
import { queryDeploymentProxyForConsole } from '../../../public/actions/apiConsole'

import type { DeploymentGetResponse } from '../../cloud-api/v1/types'
// eslint-disable-next-line import/no-restricted-paths
import type { ConsoleRequestState } from '../../../public/reducers/apiConsole'
import type { StackDeployment } from '../../ui-types'
import type { SettingsToRetrieve } from './types'

// Limit the response size to 10MB, because the response can be very large and sending it to the client
// can cause the browser to hang.

export const TEMPLATE_REQUEST: ConsoleRequestState = {
  method: `GET`,
  path: `/_search`,
  body: ``,
  advancedMode: false,
  filterBy: `regex`,
  filterRegex: ``,
  filterJq: ``,
  invertFilter: false,
}

type ProxyResponse = {
  data?: {
    body?: unknown
    status?: number
    statusText?: string
    requestDuration?: number
    contentType?: string
    headers?: Record<string, unknown>
  }
  status?: number
}

const getMappings = async (deployment: StackDeployment, settings: SettingsToRetrieve) => {
  if (settings.fields && settings.fieldsIndices) {
    const mappings = await queryDeploymentProxyForConsole(
      deployment,
      {
        ...TEMPLATE_REQUEST,
        path: '/_mapping',
        ...(settings.fieldsIndices
          ? { body: JSON.stringify({ index: settings.fieldsIndices }) }
          : {}),
      },
      false,
    )
    return mappings
  }

  // If the user doesn't want autocomplete suggestions, then clear any that exist.
  return {}
}

const getAliases = async (deployment: StackDeployment, settings: SettingsToRetrieve) => {
  if (settings.indices) {
    const aliases = await queryDeploymentProxyForConsole(
      deployment,
      {
        ...TEMPLATE_REQUEST,
        path: '/_alias',
      },
      false,
    )
    return aliases
  }

  // If the user doesn't want autocomplete suggestions, then clear any that exist.
  return {}
}

const getDataStreams = async (deployment: StackDeployment, settings: SettingsToRetrieve) => {
  if (settings.dataStreams) {
    const dataStreams = await queryDeploymentProxyForConsole(
      deployment,
      {
        ...TEMPLATE_REQUEST,
        path: '/_data_stream?name=*&expand_wildcards=all',
      },
      false,
    )
    return dataStreams
  }

  // If the user doesn't want autocomplete suggestions, then clear any that exist.
  return {}
}

const getLegacyTemplates = async (deployment: StackDeployment, settings: SettingsToRetrieve) => {
  if (settings.templates) {
    const legacyTemplates = await queryDeploymentProxyForConsole(
      deployment,
      {
        ...TEMPLATE_REQUEST,
        path: '/_template',
      },
      false,
    )
    return legacyTemplates
  }

  // If the user doesn't want autocomplete suggestions, then clear any that exist.
  return {}
}

const getIndexTemplates = async (deployment: StackDeployment, settings: SettingsToRetrieve) => {
  if (settings.templates) {
    const indexTemplates = await queryDeploymentProxyForConsole(
      deployment,
      {
        ...TEMPLATE_REQUEST,
        path: '/_index_template',
      },
      false,
    )
    return indexTemplates
  }

  // If the user doesn't want autocomplete suggestions, then clear any that exist.
  return {}
}

const getComponentTemplates = async (deployment: StackDeployment, settings: SettingsToRetrieve) => {
  if (settings.templates) {
    const componentTemplates = await queryDeploymentProxyForConsole(
      deployment,
      {
        ...TEMPLATE_REQUEST,
        path: '/_component_template',
      },
      false,
    )
    return componentTemplates
  }

  // If the user doesn't want autocomplete suggestions, then clear any that exist.
  return {}
}

export const getAutocompleteEntitiesRoute = async (
  deployment: DeploymentGetResponse,
  settings: SettingsToRetrieve,
) => {
  // Wait for all requests to complete, in case one of them fails return the successfull ones
  const results = await Promise.allSettled([
    getMappings(deployment, settings),
    getAliases(deployment, settings),
    getDataStreams(deployment, settings),
    getLegacyTemplates(deployment, settings),
    getIndexTemplates(deployment, settings),
    getComponentTemplates(deployment, settings),
  ])

  const [mappings, aliases, dataStreams, legacyTemplates, indexTemplates, componentTemplates] =
    results.map((result) => {
      // If the request was successful, return the result
      if (result.status === 'fulfilled') {
        // Extract the body/data from the response object
        const response = result.value as ProxyResponse
        return response?.data?.body ?? response
      }

      // If the request failed, log the error and return an empty object
      if (result.reason instanceof Error) {
        console.error(`Failed to retrieve autocomplete suggestions: ${result.reason.message}`)
      }

      return {}
    })

  return {
    mappings,
    aliases,
    dataStreams,
    legacyTemplates,
    indexTemplates,
    componentTemplates,
  }
}
